/**
 * THE OPERATIONAL STAGES OF A PIECE OF CONTENT.
 *
 * Emad's vocabulary, and it is a better one than the code had: it runs all the
 * way to POSTED, where `slotStage.ts` stops at the brief. The reason the old
 * rail stopped there is that a post's life is spread across three tables, and
 * nothing in the frontend was reading all three at once:
 *
 *   CalendarPlanSlot.status   PLANNED -> ... -> CLIENT_APPROVED
 *   CalendarPostBrief.status  GENERATING -> DRAFT -> IN_REVIEW -> ACCEPTED
 *   Publication.status        DRAFT -> READY -> SCHEDULED -> PUBLISHED
 *
 * A stage is the answer to "how far has this got", so it has to be read from
 * whichever of the three is furthest along. All three ride on the month payload
 * already — the slot carries `deliverable.publications[]`, and the brief comes
 * in beside it — so this costs no extra call.
 *
 * MISSED IS NOT A STAGE. It is a post whose day passed before sign-off: it says
 * WHEN something went wrong, not how far it got, and a post can be missed at
 * several different stages. It stays a flag, filtered separately, exactly as the
 * server keeps it out of the pre-signoff predicate rather than in the sequence.
 */
import {
  displayAspectRatio,
  formatLabel,
  openDeclineFor,
  type CalendarPlatform,
  type CalendarPlanSlot,
} from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { PLATFORM_ORDER, platformLabel } from "../../components/PlatformGlyph";
import {
  intendedPublicationChannels,
  isAutoPublishablePlatform,
  publicationIntegrityOf,
} from "../publicationIntegrity";

export const CONTENT_STAGES = [
  "not_started",
  "concept",
  "client_review",
  "brief",
  "production",
  "publishing",
  "scheduled",
  "partially_live",
  "posted",
] as const;
export type ContentStage = (typeof CONTENT_STAGES)[number];

export const STAGE_LABEL: Record<ContentStage, string> = {
  not_started: "Not started",
  concept: "Concept",
  client_review: "Client review",
  brief: "Creative brief",
  production: "Production",
  publishing: "Publishing",
  scheduled: "Scheduled",
  partially_live: "Partially live",
  posted: "Live",
};

/**
 * How far this post has got.
 *
 * Read from the far end backwards, because a later table always overrules an
 * earlier one: a post whose slot still says CLIENT_APPROVED but whose video is
 * already live on Instagram is POSTED, and no reading of the slot alone can
 * tell you that.
 *
 * Partial publication is the normal case, not the exception — the schema is
 * explicit that "IG posted, YouTube failed" is expected, which is why publish
 * state lives per (slot, platform) and never on the slot. A post is only LIVE
 * once every currently intended target is live; one success with work left is
 * PARTIALLY LIVE, not a completed post.
 */
export function stageOf(
  slot: CalendarPlanSlot,
  brief?: BriefRailInfo | null,
): ContentStage {
  const publication = publicationIntegrityOf(slot);
  switch (publication.state) {
    case "live":
      return "posted";
    case "partially_live":
      return "partially_live";
    case "publishing":
      return "publishing";
    case "partially_scheduled":
    case "retry_scheduled":
    case "scheduled":
      return "scheduled";
    case "failed":
    case "manual_required":
    case "ready":
    case "setup_required":
      return "publishing";
    case null:
      break;
  }
  if (slot.deliverable) return "production";

  /* A DIRECT post is ready-made content dropped onto the calendar: born
     SIGNED_OFF with no ideas, no brief and no client review, and schedulable the
     moment its media is uploaded. The three middle stages simply do not happen
     to it, so it is read from its media instead — anything else would park a
     finished asset under "Concept" and wait for an idea nobody is going to
     write. */
  if (slot.origin === "DIRECT") {
    return "production";
  }

  if (slot.status === "IN_CLIENT_REVIEW" || (slot.status === "PLANNED" && openDeclineFor(slot))) {
    return "client_review";
  }
  if (slot.status === "SIGNED_OFF") return "concept";
  if (slot.status === "CLIENT_APPROVED" && brief) return brief.status === "ACCEPTED" ? "production" : "brief";

  switch (slot.status) {
    case "CLIENT_APPROVED":
      return "brief";
    case "GENERATING_IDEAS":
    case "IDEATION":
      return "concept";
    default:
      /* PLANNED, and MISSED — which lands here because a missed post is one that
         never got past the start, and the flag beside it says the rest. */
      return "not_started";
  }
}

/** A post whose day passed before anyone signed it off. Not a stage. */
export function isMissed(slot: CalendarPlanSlot): boolean {
  return slot.status === "MISSED";
}

function platformNames(platforms: readonly CalendarPlatform[]): string {
  const names = platforms.map(platformLabel);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

/**
 * The one line a stage adds beyond its name — what is actually happening inside
 * it. "Brief" is four different situations and an operator has to tell them
 * apart without opening the post.
 */
export function stageDetail(
  slot: CalendarPlanSlot,
  brief?: BriefRailInfo | null,
): string | null {
  const publication = publicationIntegrityOf(slot);
  if (slot.deliverable) {
    if (slot.deliverable.status === "FAILED") return "Media needs attention";
    if (slot.deliverable.status !== "READY") return "Checking media";

    if (publication.state === "live") {
      const count = publication.intendedChannels.length;
      return count === 1
        ? `Live on ${platformNames(publication.intendedChannels)}`
        : `All ${count} channels live`;
    }

    const details: string[] = [];
    if (publication.publishedChannels.length > 0) {
      details.push(
        `${publication.publishedChannels.length} of ${publication.intendedChannels.length} live`,
      );
    }
    if (publication.failedChannels.length > 0) {
      details.push(`Failed on ${platformNames(publication.failedChannels)}`);
    }
    if (publication.manualChannels.length > 0) {
      details.push(`Post ${platformNames(publication.manualChannels)} manually`);
    }
    if (publication.setupChannels.length > 0) {
      details.push(`Publishing setup required for ${platformNames(publication.setupChannels)}`);
    }
    if (publication.cancelledChannels.length > 0) {
      details.push(`Create publishing again for ${platformNames(publication.cancelledChannels)}`);
    }
    if (publication.retryingChannels.length > 0) {
      details.push(`Retry scheduled on ${platformNames(publication.retryingChannels)}`);
    }
    if (publication.publishingChannels.length > 0) {
      details.push(`Publishing now on ${platformNames(publication.publishingChannels)}`);
    }
    if (publication.queuedChannels.length > 0) {
      details.push(`${platformNames(publication.queuedChannels)} queued`);
    }
    if (publication.pendingChannels.length > 0) {
      details.push(`Ready to schedule on ${platformNames(publication.pendingChannels)}`);
    }
    if (details.length > 0) return details.join(" · ");
    return "Choose channels";
  }
  if (slot.origin === "DIRECT") return "Add media and schedule";

  if (openDeclineFor(slot)) return slot.status === "PLANNED" ? "Team to align on feedback" : "Revising with client feedback";
  if (slot.status === "IN_CLIENT_REVIEW") return "Waiting for the client";
  if (slot.status === "SIGNED_OFF") return "Team draft · ready to send";
  if (slot.status === "CLIENT_APPROVED" && brief) {
    switch (brief?.status) {
      case "GENERATING":
        return "Being written";
      case "IN_REVIEW":
        return "With the client";
      case "CHANGES_REQUESTED":
        return "Changes asked for";
      case "ACCEPTED":
        return "Approved, waiting on media";
      case "FAILED":
        return "Failed, needs a retry";
      case "DRAFT":
        return "Draft ready";
      default:
        break;
    }
  }
  if (slot.status === "CLIENT_APPROVED") return "Writing creative brief";
  if (slot.status === "IDEATION") return "Three ideas, pick one";
  if (slot.status === "GENERATING_IDEAS") return "Writing ideas";
  return null;
}

/** The next truthful destination for a post, shared by month and archive rows. */
export function stageActionLabel(
  slot: CalendarPlanSlot,
  brief?: BriefRailInfo | null,
): string {
  if (slot.status === "MISSED") return "Resolve post";
  if (openDeclineFor(slot)) return "Review feedback";
  if (slot.status === "SIGNED_OFF" && slot.origin !== "DIRECT") return "Prepare to send";

  const publication = publicationIntegrityOf(slot);
  switch (publication.state) {
    case "live":
      return "View live post";
    case "partially_live":
      return publication.needsAction ? "Finish publishing" : "View publishing";
    case "failed":
      return "Retry publishing";
    case "retry_scheduled":
      return "View retry";
    case "publishing":
      return "View publishing";
    case "partially_scheduled":
      return "Finish scheduling";
    case "manual_required":
      return "Post manually";
    case "setup_required":
      return "Open publishing";
    case "scheduled":
      return publication.needsAction ? "Finish scheduling" : "View schedule";
    case "ready":
      return publication.intendedChannels.length === 0 ? "Choose channels" : "Schedule post";
    case null:
      break;
  }

  switch (stageOf(slot, brief)) {
    case "not_started":
      return "Open plan";
    case "concept":
      return "Review concepts";
    case "client_review":
      return "View proposal";
    case "brief":
      return "Open brief";
    case "production":
      return "Open production";
    case "publishing":
      return "Open publishing";
    case "scheduled":
      return "View schedule";
    case "partially_live":
      return "Finish publishing";
    case "posted":
      return "View live post";
  }
}

export type ContentStageDestination = "concept" | "brief" | "publishing";

/** One route decision for grid rows, list rows, and archived records. */
export function stageDestination(
  slot: CalendarPlanSlot,
  brief?: BriefRailInfo | null,
): ContentStageDestination {
  const stage = stageOf(slot, brief);
  if (stage === "brief") return "brief";
  if (
    slot.origin === "DIRECT" ||
    stage === "production" ||
    stage === "publishing" ||
    stage === "scheduled" ||
    stage === "partially_live" ||
    stage === "posted"
  ) {
    return "publishing";
  }
  return "concept";
}

/* ===========================================================================
   FORMAT
   =========================================================================== */

/**
 * The format, as the ONE bucket the month is budgeted in.
 *
 * Three orthogonal axes on the row — media type, orientation, length band — but
 * only three combinations are ever counted, because statics are deliberately
 * not split by orientation: "which way up a static sits is a design call per
 * post, not a monthly budget."
 */
export function formatBucket(slot: CalendarPlanSlot): "static" | "vertical" | "horizontal" {
  if (slot.mediaType === "STATIC") return "static";
  return slot.orientation === "HORIZONTAL" ? "horizontal" : "vertical";
}

/** Compact card label: media first, then the exact canvas. */
export function formatShort(slot: CalendarPlanSlot): string {
  return `${slot.mediaType === "STATIC" ? "Static" : "Video"} · ${displayAspectRatio(slot)}`;
}

/** The format in full, for a row that has the width for it. */
export function formatLine(slot: CalendarPlanSlot): string {
  return formatLabel(slot);
}

/* ===========================================================================
   CHANNELS
   =========================================================================== */

/**
 * Where this post will live.
 *
 * THE TRAP THIS EXISTS TO CLOSE: channels are on two different rows depending
 * on the post, and on neither of them before sign-off.
 *
 *   DIRECT   -> slot.channels          (set when the quick post is created)
 *   PIPELINE -> the ACCEPTED idea      (slot.channels stays [] for these, so
 *                                       the two sources can never disagree)
 *
 * A pipeline post before sign-off has no accepted idea, therefore no channels,
 * therefore nothing to draw. That is not a loading state and not an empty
 * state — the answer genuinely does not exist yet, and a row that prints a dash
 * is telling the truth where one that prints "None" is not.
 */
export function channelsOf(slot: CalendarPlanSlot): CalendarPlatform[] {
  return intendedPublicationChannels(slot);
}

export interface ChannelChip {
  platform: CalendarPlatform;
  /** False for channels that need a person to publish them. */
  publishable: boolean;
  /** Per-target state. Manual/pending only become actions once media is ready. */
  state:
    | "posted"
    | "publishing"
    | "scheduled"
    | "retrying"
    | "failed"
    | "manual"
    | "setup"
    | "cancelled"
    | "pending"
    | null;
}

/** Plain-language state paired with a platform mark for assistive technology. */
export function channelStateText(channel: ChannelChip): string | null {
  switch (channel.state) {
    case "posted":
      return "live";
    case "publishing":
      return "publishing now";
    case "scheduled":
      return "scheduled";
    case "retrying":
      return "automatic retry scheduled";
    case "failed":
      return "failed to post";
    case "manual":
      return "manual posting required";
    case "setup":
      return "publishing setup required";
    case "cancelled":
      return "publication cancelled; create it again";
    case "pending":
      return "ready to schedule";
    case null:
      return null;
  }
}

export function channelChips(slot: CalendarPlanSlot): ChannelChip[] {
  const publication = publicationIntegrityOf(slot);
  return publication.channels
    .slice()
    .sort(
      (a, b) =>
        PLATFORM_ORDER.indexOf(a.platform) - PLATFORM_ORDER.indexOf(b.platform),
    )
    .map((channel) => {
      const state: ChannelChip["state"] =
        channel.state === "published"
          ? "posted"
          : channel.state === "publishing"
            ? "publishing"
            : channel.state === "queued"
              ? "scheduled"
              : channel.state === "retrying"
                ? "retrying"
                : channel.state === "failed"
                  ? "failed"
                  : publication.state === null
                    ? null
                    : channel.state;
      /* No prettier label: these render as uppercase mono chips, which is what
         the label step in this system is, so "TikTok" and "Instagram" came out
         indistinguishable from the raw enum. A mapping that changes nothing is
         worse than none — it reads as if the display name were handled. */
      return {
        platform: channel.platform,
        publishable: isAutoPublishablePlatform(channel.platform),
        state,
      };
    });
}
